import * as THREE from 'three';
import { bakeColored, createInstancedMesh, type InstanceTransform } from './batch';
import { createGlowLayer, lampGlowSpots } from './glow';
import { mesh, rod, roundedBox } from './helpers';
import type { ParkMaterials } from './materials';

export type Placement = { x: number; z: number; rotY?: number; scale?: number; tint?: number };

function place(list: Placement[], y: number, options: { jitterRot?: boolean } = {}): InstanceTransform[] {
  return list.map((item, index) => ({
    position: [item.x, y, item.z] as [number, number, number],
    rotation: [0, item.rotY ?? (options.jitterRot ? (index * 1.7) % (Math.PI * 2) : 0), 0] as [number, number, number],
    scale: item.scale ?? 1,
    color: item.tint,
  }));
}

/** Bakes a multi-colour prototype once and instances it with the shared vertex-colour material. */
export function instancedProp(
  proto: THREE.Object3D,
  material: THREE.Material,
  transforms: InstanceTransform[],
  options: { cast?: boolean; receive?: boolean } = {},
): THREE.InstancedMesh {
  return createInstancedMesh(bakeColored(proto), material, transforms, options);
}

function roundTreeProto(mats: ParkMaterials): THREE.Group {
  const proto = new THREE.Group();
  proto.add(mesh(new THREE.CylinderGeometry(0.09, 0.13, 0.62, 7), mats.trunk, { position: [0, 0.31, 0] }));
  proto.add(mesh(new THREE.SphereGeometry(0.52, 9, 7), mats.foliage, { position: [0, 0.98, 0], scale: [1, 0.92, 1] }));
  proto.add(mesh(new THREE.SphereGeometry(0.38, 8, 6), mats.foliageLight, { position: [0.26, 1.22, 0.12] }));
  proto.add(mesh(new THREE.SphereGeometry(0.34, 8, 6), mats.foliageDark, { position: [-0.24, 1.14, -0.16] }));
  proto.add(mesh(new THREE.SphereGeometry(0.3, 7, 5), mats.foliage, { position: [0.02, 1.42, -0.02] }));
  return proto;
}

function pineProto(mats: ParkMaterials): THREE.Group {
  const proto = new THREE.Group();
  proto.add(mesh(new THREE.CylinderGeometry(0.08, 0.11, 0.5, 7), mats.trunk, { position: [0, 0.25, 0] }));
  proto.add(mesh(new THREE.ConeGeometry(0.62, 0.78, 8), mats.pine, { position: [0, 0.78, 0] }));
  proto.add(mesh(new THREE.ConeGeometry(0.48, 0.7, 8), mats.pine, { position: [0, 1.22, 0] }));
  proto.add(mesh(new THREE.ConeGeometry(0.32, 0.62, 8), mats.pineLight, { position: [0, 1.66, 0] }));
  return proto;
}

export function createTrees(
  mats: ParkMaterials,
  round: Placement[],
  pines: Placement[],
  parkTop: number,
): THREE.Group {
  const group = new THREE.Group();
  if (round.length) {
    group.add(instancedProp(roundTreeProto(mats), mats.baked, place(round, parkTop, { jitterRot: true })));
  }
  if (pines.length) {
    group.add(instancedProp(pineProto(mats), mats.baked, place(pines, parkTop, { jitterRot: true })));
  }
  return group;
}

export function createShrubs(mats: ParkMaterials, spots: Placement[], parkTop: number): THREE.InstancedMesh {
  const proto = new THREE.Group();
  proto.add(mesh(new THREE.SphereGeometry(0.3, 8, 6), mats.hedge, { position: [0, 0.22, 0], scale: [1, 0.78, 1] }));
  proto.add(mesh(new THREE.SphereGeometry(0.2, 7, 5), mats.foliageLight, { position: [0.16, 0.3, 0.1], scale: [1, 0.8, 1] }));
  return instancedProp(proto, mats.baked, place(spots, parkTop, { jitterRot: true }));
}

/** Hedge blocks used as a soft boundary along the back and sides of the park. */
export function createHedges(mats: ParkMaterials, spots: Placement[], parkTop: number): THREE.InstancedMesh {
  const proto = new THREE.Group();
  proto.add(mesh(roundedBox(1.15, 0.46, 0.42, 0.14), mats.hedge, { position: [0, 0.23, 0] }));
  proto.add(mesh(roundedBox(0.7, 0.2, 0.3, 0.1), mats.foliageLight, { position: [0.12, 0.5, 0] }));
  return instancedProp(proto, mats.baked, place(spots, parkTop));
}

/** Height of the lantern glass centre above the lamp base. */
export const LAMP_BULB_Y = 1.3;

/** Player-placed benches use entity rotation only — no baked extra yaw. */
export const BENCH_PLACEMENT_YAW = 0;

/** Lamp proto base bottom is at local y=0 so parkTop is ground contact. */
export const LAMP_BASE_BOTTOM_Y = 0;

/** Nudge a placed prop so its non-glow geometry sits on `groundY`. */
export function snapPlacedPropToGround(object: THREE.Object3D, groundY: number): number {
  object.updateMatrixWorld(true);
  let minY = Infinity;
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) {
      return;
    }
    const material = Array.isArray(child.material) ? child.material[0] : child.material;
    if (material && material.blending === THREE.AdditiveBlending) {
      return;
    }
    child.geometry.computeBoundingBox();
    const box = child.geometry.boundingBox;
    if (!box) {
      return;
    }
    const world = box.clone().applyMatrix4(child.matrixWorld);
    if (world.min.y < minY) {
      minY = world.min.y;
    }
  });
  if (!Number.isFinite(minY)) {
    return 0;
  }
  const delta = groundY - minY;
  object.position.y += delta;
  return delta;
}

/**
 * COL-011B lamp: taller post, gold collar, a proper four-sided lantern with a pitched cap and a
 * bigger glass block, plus a camera-facing halo and a warm ground pool that only show at night.
 */
export function createLamps(mats: ParkMaterials, spots: Placement[], parkTop: number): THREE.Group {
  const group = new THREE.Group();
  const proto = new THREE.Group();
  proto.add(mesh(new THREE.CylinderGeometry(0.12, 0.16, 0.12, 8), mats.lampPost, { position: [0, 0.06, 0] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.03, 0.05, 1.05, 7), mats.lampPost, { position: [0, 0.64, 0] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.05, 0.04, 0.05, 7), mats.gold, { position: [0, 1.17, 0] }));
  proto.add(mesh(new THREE.BoxGeometry(0.22, 0.03, 0.22), mats.lampPost, { position: [0, 1.2, 0] }));
  for (const [x, z] of [
    [-0.1, -0.1],
    [0.1, -0.1],
    [-0.1, 0.1],
    [0.1, 0.1],
  ] as const) {
    proto.add(mesh(new THREE.BoxGeometry(0.02, 0.22, 0.02), mats.lampPost, { position: [x, 1.31, z] }));
  }
  proto.add(mesh(new THREE.ConeGeometry(0.2, 0.13, 4), mats.lampPost, { position: [0, 1.49, 0], rotation: [0, Math.PI / 4, 0] }));
  proto.add(mesh(new THREE.SphereGeometry(0.035, 6, 5), mats.gold, { position: [0, 1.58, 0] }));
  group.add(instancedProp(proto, mats.baked, place(spots, parkTop)));

  const bulbs = createInstancedMesh(
    new THREE.BoxGeometry(0.16, 0.19, 0.16),
    mats.lamp,
    spots.map((spot) => ({ position: [spot.x, parkTop + LAMP_BULB_Y, spot.z] as [number, number, number] })),
    { cast: false, receive: false },
  );
  group.add(bulbs);

  group.add(
    createGlowLayer(
      mats.glowWarm,
      spots.flatMap((spot) => lampGlowSpots(spot.x, parkTop + LAMP_BULB_Y, spot.z, 1.1, 1.05, parkTop + 0.02)),
    ),
  );
  return group;
}

export function createBenches(mats: ParkMaterials, spots: Placement[], parkTop: number): THREE.InstancedMesh {
  const proto = new THREE.Group();
  proto.add(mesh(roundedBox(0.86, 0.06, 0.3, 0.02), mats.wood, { position: [0, 0.24, 0] }));
  proto.add(mesh(roundedBox(0.86, 0.07, 0.05, 0.02), mats.wood, { position: [0, 0.38, -0.14], rotation: [0.12, 0, 0] }));
  proto.add(mesh(roundedBox(0.86, 0.07, 0.05, 0.02), mats.wood, { position: [0, 0.5, -0.16], rotation: [0.12, 0, 0] }));
  for (const x of [-0.34, 0.34]) {
    proto.add(mesh(new THREE.BoxGeometry(0.06, 0.24, 0.26), mats.iron, { position: [x, 0.12, 0.0] }));
    proto.add(mesh(new THREE.BoxGeometry(0.06, 0.36, 0.05), mats.iron, { position: [x, 0.4, -0.16], rotation: [0.12, 0, 0] }));
  }
  return instancedProp(proto, mats.baked, place(spots, parkTop));
}

export function createBins(mats: ParkMaterials, spots: Placement[], parkTop: number): THREE.InstancedMesh {
  const proto = new THREE.Group();
  proto.add(mesh(new THREE.CylinderGeometry(0.13, 0.11, 0.34, 8), mats.lampPost, { position: [0, 0.17, 0] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.06, 8), mats.red, { position: [0, 0.37, 0] }));
  return instancedProp(proto, mats.baked, place(spots, parkTop));
}

const BLOOM_COLORS = [0xf0879e, 0xf7d35c, 0xe5514f, 0xfff6e6, 0x9a6fc9, 0xf08a3c];

/** Round raised beds: pale curb, dark soil, leafy dome and a ring of bright blooms. */
export function createFlowerBeds(mats: ParkMaterials, spots: Placement[], parkTop: number): THREE.Group {
  const group = new THREE.Group();
  const proto = new THREE.Group();
  proto.add(mesh(new THREE.CylinderGeometry(0.46, 0.5, 0.12, 12), mats.curb, { position: [0, 0.06, 0] }));
  proto.add(mesh(new THREE.CylinderGeometry(0.4, 0.4, 0.06, 12), mats.woodDark, { position: [0, 0.14, 0] }));
  proto.add(mesh(new THREE.SphereGeometry(0.37, 9, 6), mats.foliageDark, { position: [0, 0.16, 0], scale: [1, 0.42, 1] }));
  group.add(instancedProp(proto, mats.baked, place(spots, parkTop)));

  const blooms: InstanceTransform[] = [];
  spots.forEach((spot, spotIndex) => {
    const count = 7;
    for (let i = 0; i < count; i += 1) {
      const angle = (i / count) * Math.PI * 2 + spotIndex * 0.4;
      const radius = 0.16 + (i % 2) * 0.1;
      blooms.push({
        position: [spot.x + Math.cos(angle) * radius, parkTop + 0.3 - (i % 2) * 0.04, spot.z + Math.sin(angle) * radius],
        scale: 0.85 + (i % 3) * 0.12,
        color: BLOOM_COLORS[(i + spotIndex) % BLOOM_COLORS.length],
      });
    }
    blooms.push({ position: [spot.x, parkTop + 0.34, spot.z], scale: 1.1, color: BLOOM_COLORS[(spotIndex + 3) % BLOOM_COLORS.length] });
  });
  const bloomMaterial = mats.horseCoat.clone();
  bloomMaterial.roughness = 0.7;
  group.add(createInstancedMesh(new THREE.SphereGeometry(0.065, 6, 5), bloomMaterial, blooms, { cast: false }));
  return group;
}

/**
 * Low white picket fence along a polyline. Posts, rails and pickets are baked to one mesh.
 * Gaps are expressed simply by passing separate segments.
 */
export function createPicketFence(
  mats: ParkMaterials,
  segments: Array<[THREE.Vector2, THREE.Vector2]>,
  parkTop: number,
): THREE.Mesh {
  const proto = new THREE.Group();
  for (const [from, to] of segments) {
    const length = from.distanceTo(to);
    const angle = Math.atan2(to.x - from.x, to.y - from.y);
    const local = new THREE.Group();
    local.position.set((from.x + to.x) / 2, 0, (from.y + to.y) / 2);
    local.rotation.y = angle;
    local.add(mesh(new THREE.BoxGeometry(0.05, 0.05, length), mats.fence, { position: [0, 0.22, 0] }));
    local.add(mesh(new THREE.BoxGeometry(0.05, 0.05, length), mats.fence, { position: [0, 0.42, 0] }));
    const posts = Math.max(2, Math.round(length / 1.4) + 1);
    for (let i = 0; i < posts; i += 1) {
      const z = -length / 2 + (length / (posts - 1)) * i;
      local.add(mesh(new THREE.BoxGeometry(0.1, 0.62, 0.1), mats.fence, { position: [0, 0.31, z] }));
      local.add(mesh(new THREE.SphereGeometry(0.06, 6, 5), mats.fence, { position: [0, 0.64, z] }));
    }
    const pickets = Math.floor(length / 0.32);
    for (let i = 0; i < pickets; i += 1) {
      const z = -length / 2 + 0.16 + i * 0.32;
      local.add(mesh(new THREE.BoxGeometry(0.03, 0.44, 0.08), mats.fence, { position: [0.04, 0.3, z] }));
      local.add(mesh(new THREE.ConeGeometry(0.055, 0.08, 4), mats.fence, { position: [0.04, 0.56, z], rotation: [0, Math.PI / 4, 0] }));
    }
    proto.add(local);
  }
  const fence = new THREE.Mesh(bakeColored(proto), mats.baked);
  fence.position.y = parkTop;
  fence.castShadow = true;
  fence.receiveShadow = false;
  return fence;
}

/** Festive bunting: alternating pennants hung along a gentle sag between two points. */
export function createBunting(
  mats: ParkMaterials,
  from: THREE.Vector3,
  to: THREE.Vector3,
  count: number,
  colors: THREE.MeshStandardMaterial[] = [mats.red, mats.yellow, mats.blue, mats.cream],
): THREE.Mesh {
  const proto = new THREE.Group();
  const line = new THREE.Vector3().subVectors(to, from);
  proto.add(rod(from, to, 0.012, mats.woodDark, 4));
  const yaw = Math.atan2(line.x, line.z);
  for (let i = 0; i < count; i += 1) {
    const t = (i + 0.5) / count;
    const sag = Math.sin(t * Math.PI) * 0.16;
    const point = new THREE.Vector3().lerpVectors(from, to, t);
    proto.add(
      mesh(new THREE.ConeGeometry(0.09, 0.2, 3), colors[i % colors.length]!, {
        position: [point.x, point.y - 0.14 - sag, point.z],
        rotation: [0, yaw, Math.PI],
        scale: [1, 1, 0.2],
      }),
    );
  }
  const bunting = new THREE.Mesh(bakeColored(proto), mats.baked);
  bunting.castShadow = false;
  bunting.receiveShadow = false;
  return bunting;
}
